import type { DailyLog, TaskItem, TopicEntry, TopicUpdate } from '../services/dailylog';
import {
  formatDate,
  formatTimestamp,
  formatTimeAgo,
  getTaskStatusClass,
  getPriorityClass,
  getCurrentDailyLog,
  getPreviousDailyLog,
  TaskStatusValues,
} from '../services/dailylog';

/**
 * Create a daily log display component
 */
export function createDailyLogDisplay(
  log: DailyLog,
  onRefresh?: () => void,
): HTMLElement {
  const container = document.createElement('div');
  container.style.cssText = 'margin-bottom: 2rem;';

  // Header
  const header = document.createElement('div');
  header.style.cssText =
    'margin-bottom: 1rem; padding-bottom: 0.5rem; border-bottom: 2px solid #e0e0e0;';

  // Create header content wrapper
  const headerContent = document.createElement('div');
  headerContent.style.cssText = 'display: flex; justify-content: space-between; align-items: start;';

  // Left side: title and metadata
  const leftContent = document.createElement('div');
  leftContent.style.cssText = 'flex: 1;';
  leftContent.innerHTML = `
    <h2 style="margin: 0 0 0.5rem 0; font-size: 1.5rem; color: #333;">
      Daily Log - ${formatDate(log.log_date)}
    </h2>
    ${log.summary ? `<p style="margin: 0; color: #666; font-size: 0.95rem;">${escapeHtml(log.summary)}</p>` : ''}
    <p style="margin: 0.5rem 0 0 0; color: #999; font-size: 0.85rem; font-style: italic;">
      Last updated ${formatTimeAgo(log.updated_at)}
    </p>
  `;
  headerContent.appendChild(leftContent);

  // Right side: refresh button
  if (onRefresh) {
    const refreshButton = document.createElement('button');
    refreshButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
      </svg>
    `;
    refreshButton.style.cssText = `
      padding: 0.5rem;
      background: white;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      cursor: pointer;
      color: #6b7280;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    `;
    refreshButton.title = 'Refresh daily log';
    refreshButton.onclick = () => {
      // Add spinning animation
      refreshButton.style.transform = 'rotate(360deg)';
      refreshButton.style.transition = 'transform 0.5s ease';
      setTimeout(() => {
        refreshButton.style.transform = '';
      }, 500);
      onRefresh();
    };
    refreshButton.onmouseover = () => {
      refreshButton.style.background = '#f3f4f6';
      refreshButton.style.borderColor = '#9ca3af';
      refreshButton.style.color = '#374151';
    };
    refreshButton.onmouseout = () => {
      refreshButton.style.background = 'white';
      refreshButton.style.borderColor = '#d1d5db';
      refreshButton.style.color = '#6b7280';
    };
    headerContent.appendChild(refreshButton);
  }

  header.appendChild(headerContent);
  container.appendChild(header);

  // Stats
  const stats = document.createElement('div');
  stats.style.cssText = 'display: flex; gap: 1.5rem; margin-bottom: 1.5rem; flex-wrap: wrap;';
  stats.innerHTML = `
    <div style="padding: 0.75rem 1rem; background: #f0f9ff; border-radius: 8px; border-left: 3px solid #0ea5e9;">
      <div style="font-size: 0.75rem; color: #0369a1; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Open Tasks</div>
      <div style="font-size: 1.5rem; font-weight: bold; color: #0c4a6e;">${log.audit.stats.tasks_open}</div>
    </div>
    <div style="padding: 0.75rem 1rem; background: #f0fdf4; border-radius: 8px; border-left: 3px solid #10b981;">
      <div style="font-size: 0.75rem; color: #047857; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Completed Today</div>
      <div style="font-size: 1.5rem; font-weight: bold; color: #065f46;">${log.audit.stats.tasks_done_today}</div>
    </div>
    <div style="padding: 0.75rem 1rem; background: #fef3c7; border-radius: 8px; border-left: 3px solid #f59e0b;">
      <div style="font-size: 0.75rem; color: #d97706; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Topics</div>
      <div style="font-size: 1.5rem; font-weight: bold; color: #92400e;">${log.topics.length}</div>
    </div>
    <div style="padding: 0.75rem 1rem; background: #fce7f3; border-radius: 8px; border-left: 3px solid #ec4899;">
      <div style="font-size: 0.75rem; color: #db2777; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Updates</div>
      <div style="font-size: 1.5rem; font-weight: bold; color: #831843;">${log.audit.stats.updates_applied}</div>
    </div>
  `;
  container.appendChild(stats);

  // Tasks Section
  if (log.tasks.length > 0) {
    const tasksSection = createTasksSection(log.tasks);
    container.appendChild(tasksSection);
  }

  // Topics Section
  if (log.topics.length > 0) {
    const topicsSection = createTopicsSection(log.topics);
    container.appendChild(topicsSection);
  }

  // Empty state
  if (log.tasks.length === 0 && log.topics.length === 0) {
    const emptyState = document.createElement('div');
    emptyState.style.cssText =
      'padding: 2rem; text-align: center; background: #f9fafb; border-radius: 8px; color: #6b7280;';
    emptyState.innerHTML = `
      <p style="margin: 0; font-size: 1rem;">No tasks or topics logged yet today.</p>
      <p style="margin: 0.5rem 0 0 0; font-size: 0.875rem;">Your daily log will be populated as you process emails and updates.</p>
    `;
    container.appendChild(emptyState);
  }

  return container;
}

/**
 * Create tasks section
 */
function createTasksSection(tasks: TaskItem[]): HTMLElement {
  const section = document.createElement('div');
  section.style.cssText = 'margin-bottom: 2rem;';

  const header = document.createElement('h3');
  header.textContent = 'Tasks';
  header.style.cssText = 'margin: 0 0 1rem 0; font-size: 1.25rem; color: #333;';
  section.appendChild(header);

  // Group tasks by status
  const openTasks = tasks.filter((t) => t.status === TaskStatusValues.OPEN);
  const inProgressTasks = tasks.filter((t) => t.status === TaskStatusValues.IN_PROGRESS);
  const doneTasks = tasks.filter((t) => t.status === TaskStatusValues.DONE);
  const otherTasks = tasks.filter(
    (t) =>
      t.status !== TaskStatusValues.OPEN &&
      t.status !== TaskStatusValues.IN_PROGRESS &&
      t.status !== TaskStatusValues.DONE,
  );

  const taskGroups = [
    { title: 'In Progress', tasks: inProgressTasks, variant: 'primary' },
    { title: 'Open', tasks: openTasks, variant: 'warning' },
    { title: 'Completed', tasks: doneTasks, variant: 'success' },
    { title: 'Other', tasks: otherTasks, variant: 'neutral' },
  ];

  taskGroups.forEach((group) => {
    if (group.tasks.length > 0) {
      const groupDiv = document.createElement('div');
      groupDiv.style.cssText = 'margin-bottom: 1.5rem;';

      const groupHeader = document.createElement('h4');
      groupHeader.textContent = `${group.title} (${group.tasks.length})`;
      groupHeader.style.cssText = 'margin: 0 0 0.75rem 0; font-size: 1rem; color: #555;';
      groupDiv.appendChild(groupHeader);

      const taskList = document.createElement('div');
      taskList.style.cssText = 'display: flex; flex-direction: column; gap: 0.75rem;';

      group.tasks.forEach((task) => {
        const taskCard = createTaskCard(task);
        taskList.appendChild(taskCard);
      });

      groupDiv.appendChild(taskList);
      section.appendChild(groupDiv);
    }
  });

  return section;
}

/**
 * Create a task card
 */
function createTaskCard(task: TaskItem): HTMLElement {
  const card = document.createElement('div');
  card.style.cssText =
    'padding: 1rem; background: white; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);';

  const statusClass = getTaskStatusClass(task.status);
  const priorityClass = getPriorityClass(task.priority);

  card.innerHTML = `
    <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 0.5rem;">
      <div style="flex: 1;">
        <div style="font-weight: 600; color: #111; margin-bottom: 0.25rem;">${escapeHtml(task.title)}</div>
        ${task.owner ? `<div style="font-size: 0.875rem; color: #6b7280;">Owner: ${escapeHtml(task.owner)}</div>` : ''}
      </div>
      <div style="display: flex; gap: 0.5rem; align-items: center;">
        <span style="padding: 0.25rem 0.75rem; background: var(--sl-color-${statusClass}-100); color: var(--sl-color-${statusClass}-700); border-radius: 12px; font-size: 0.75rem; font-weight: 600; text-transform: uppercase;">
          ${task.status.replace('_', ' ')}
        </span>
        ${task.priority !== 'med' ? `<span style="padding: 0.25rem 0.75rem; background: var(--sl-color-${priorityClass}-100); color: var(--sl-color-${priorityClass}-700); border-radius: 12px; font-size: 0.75rem; font-weight: 600;">
          ${task.priority.toUpperCase()}
        </span>` : ''}
      </div>
    </div>
    ${task.due ? `<div style="font-size: 0.875rem; color: #ef4444; margin-bottom: 0.5rem;">📅 Due: ${task.due}</div>` : ''}
    ${task.notes ? `<div style="font-size: 0.875rem; color: #6b7280; margin-top: 0.5rem; padding-top: 0.5rem; border-top: 1px solid #f3f4f6;">${escapeHtml(task.notes)}</div>` : ''}
  `;

  return card;
}

/**
 * Create topics section
 */
function createTopicsSection(topics: TopicEntry[]): HTMLElement {
  const section = document.createElement('div');
  section.style.cssText = 'margin-bottom: 2rem;';

  const header = document.createElement('h3');
  header.textContent = 'Topics';
  header.style.cssText = 'margin: 0 0 1rem 0; font-size: 1.25rem; color: #333;';
  section.appendChild(header);

  const topicList = document.createElement('div');
  topicList.style.cssText = 'display: flex; flex-direction: column; gap: 1rem;';

  topics.forEach((topic) => {
    const topicCard = createTopicCard(topic);
    topicList.appendChild(topicCard);
  });

  section.appendChild(topicList);
  return section;
}

/**
 * Create a topic card
 */
function createTopicCard(topic: TopicEntry): HTMLElement {
  const card = document.createElement('div');
  card.style.cssText =
    'padding: 1rem; background: white; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);';

  const topicHeader = document.createElement('div');
  topicHeader.style.cssText = 'margin-bottom: 0.75rem;';
  topicHeader.innerHTML = `
    <div style="font-weight: 600; font-size: 1.1rem; color: #111; margin-bottom: 0.25rem;">${escapeHtml(topic.title)}</div>
    ${topic.updates.length > 0 ? `<div style="font-size: 0.875rem; color: #6b7280;">${topic.updates.length} update${topic.updates.length !== 1 ? 's' : ''}</div>` : ''}
  `;
  card.appendChild(topicHeader);

  if (topic.updates.length > 0) {
    const updatesList = document.createElement('div');
    updatesList.style.cssText = 'display: flex; flex-direction: column; gap: 0.5rem;';

    // Show only the last 3 updates by default
    const visibleUpdates = topic.updates.slice(-3);

    visibleUpdates.forEach((update: TopicUpdate) => {
      const updateDiv = document.createElement('div');
      updateDiv.style.cssText = 'padding: 0.5rem; background: #f9fafb; border-radius: 6px;';

      updateDiv.innerHTML = `
        <div style="display: flex; gap: 0.5rem; align-items: start;">
          <span style="font-size: 1rem;">📌</span>
          <div style="flex: 1;">
            <div style="font-size: 0.875rem; color: #374151;">${escapeHtml(update.detail)}</div>
            <div style="font-size: 0.75rem; color: #9ca3af; margin-top: 0.25rem;">${formatTimestamp(update.ts)} • ${update.kind}</div>
          </div>
        </div>
      `;
      updatesList.appendChild(updateDiv);
    });

    if (topic.updates.length > 3) {
      const moreDiv = document.createElement('div');
      moreDiv.style.cssText =
        'font-size: 0.875rem; color: #6b7280; font-style: italic; padding: 0.5rem;';
      moreDiv.textContent = `... and ${topic.updates.length - 3} more update(s)`;
      updatesList.appendChild(moreDiv);
    }

    card.appendChild(updatesList);
  }

  return card;
}

/**
 * Load and display the current daily log
 */
export async function loadAndDisplayDailyLog(
  containerId: string = 'dailylog-container',
  contextName: string = 'default',
): Promise<void> {
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container with id "${containerId}" not found`);
    return;
  }

  // Track the current view state
  let showingPrevious = false;

  // Function to render the log with toggle
  async function renderLog(usePrevious: boolean = false) {
    if (!container) return;
    showingPrevious = usePrevious;

    // Show loading state
    container.innerHTML = `
      <div style="padding: 2rem; text-align: center; color: #6b7280;">
        <div style="font-size: 1rem;">Loading daily log...</div>
      </div>
    `;

    try {
      // Fetch the appropriate log
      const log = usePrevious
        ? await getPreviousDailyLog(contextName)
        : await getCurrentDailyLog(contextName);

      if (!container) return;

      if (!log) {
        container.innerHTML = `
          <div style="padding: 2rem; text-align: center; color: #6b7280;">
            <div style="font-size: 1rem; font-weight: 600;">No previous daily log found</div>
            <div style="font-size: 0.875rem; margin-top: 0.5rem;">There is no daily log for yesterday.</div>
          </div>
        `;
        return;
      }

      // Clear container
      container.innerHTML = '';

      // Create toggle controls
      const toggleContainer = document.createElement('div');
      toggleContainer.style.cssText =
        'display: flex; gap: 0.5rem; margin-bottom: 1rem; padding: 0.75rem; background: #f9fafb; border-radius: 8px; align-items: center;';

      const label = document.createElement('span');
      label.textContent = 'View:';
      label.style.cssText = 'font-size: 0.875rem; color: #6b7280; font-weight: 600;';
      toggleContainer.appendChild(label);

      // Current button
      const currentButton = document.createElement('button');
      currentButton.textContent = 'Today';
      currentButton.style.cssText = `
        padding: 0.5rem 1rem;
        border: 1px solid ${!usePrevious ? '#3b82f6' : '#d1d5db'};
        background: ${!usePrevious ? '#3b82f6' : 'white'};
        color: ${!usePrevious ? 'white' : '#374151'};
        border-radius: 6px;
        font-size: 0.875rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      `;
      currentButton.onclick = () => {
        if (!showingPrevious) return;
        renderLog(false);
      };
      currentButton.onmouseover = () => {
        if (!usePrevious) return;
        currentButton.style.background = '#f3f4f6';
      };
      currentButton.onmouseout = () => {
        if (!usePrevious) return;
        currentButton.style.background = 'white';
      };
      toggleContainer.appendChild(currentButton);

      // Previous button
      const previousButton = document.createElement('button');
      previousButton.textContent = 'Previous';
      previousButton.style.cssText = `
        padding: 0.5rem 1rem;
        border: 1px solid ${usePrevious ? '#3b82f6' : '#d1d5db'};
        background: ${usePrevious ? '#3b82f6' : 'white'};
        color: ${usePrevious ? 'white' : '#374151'};
        border-radius: 6px;
        font-size: 0.875rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      `;
      previousButton.onclick = () => {
        if (showingPrevious) return;
        renderLog(true);
      };
      previousButton.onmouseover = () => {
        if (usePrevious) return;
        previousButton.style.background = '#f3f4f6';
      };
      previousButton.onmouseout = () => {
        if (usePrevious) return;
        previousButton.style.background = 'white';
      };
      toggleContainer.appendChild(previousButton);

      container.appendChild(toggleContainer);

      // Display the log with refresh callback
      const logDisplay = createDailyLogDisplay(log, () => renderLog(usePrevious));
      container.appendChild(logDisplay);
    } catch (error: unknown) {
      if (!container) return;
      console.error('Error loading daily log:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      container.innerHTML = `
        <div style="padding: 2rem; text-align: center; color: #ef4444;">
          <div style="font-size: 1rem; font-weight: 600;">Error loading daily log</div>
          <div style="font-size: 0.875rem; margin-top: 0.5rem;">${escapeHtml(errorMessage)}</div>
        </div>
      `;
    }
  }

  // Initial render - show current log
  await renderLog(false);
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
